<template>
  <span>

    <b-container fluid>
      <b-row class="table-header">
        <b-col cols="12" sm="6" lg="3">
          <b-form-group v-if="context === 'selectMany'">
            <b-button
              variant="outline-secondary"
              @click="removeAll">
              <i class="fas fa-minus"></i>
              Remove All
            </b-button>
          </b-form-group>
        </b-col>

        <b-col cols="12" sm="6" lg="3">
          <b-form-group v-if="showTypeSelect && showModuleSelect">
            <b-form-select
              :options="mediaTypes"
              :disabled="fetching"
              v-model="meta.type"
              @change="filterByType"/>
          </b-form-group>
        </b-col>

        <!-- Confusing logic to push the type select and module select to the right -->
        <b-col cols="12" sm="6" lg="3">
          <b-form-group v-if="showTypeSelect && !showModuleSelect">
            <b-form-select
              :options="mediaTypes"
              :disabled="fetching"
              v-model="meta.type"
              @change="filterByType"/>
          </b-form-group>
          <b-form-group v-if="!showTypeSelect && showModuleSelect">
            <b-form-select
              :options="moduleNames"
              :disabled="fetching"
              v-model="meta.module"
              @change="filterByModule"/>
          </b-form-group>
          <b-form-group v-if="showTypeSelect && showModuleSelect">
            <b-form-select
              :options="moduleNames"
              :disabled="fetching"
              v-model="meta.module"
              @change="filterByModule"/>
          </b-form-group>
        </b-col>

        <b-col cols="12" sm="6" lg="3">
          <b-form-group>
            <b-form-input
              ref="search"
              v-model="meta.search"
              :disabled="fetching"
              placeholder="Search by name"
              @keyup.native="search"
              @input="searchChanged"/>
          </b-form-group>
        </b-col>
      </b-row>

      <no-data v-if="noData" message="No media found..."/>

      <b-table
        v-if="tableData.length"
        :hover="rowClickEnabled && !fetching"
        :responsive="true"
        :busy="fetching"
        :items="tableData"
        :fields="formFields"
        :per-page="Number(meta.per_page)"
        :sort-by="meta.order"
        :sort-desc="meta.desc"
        :no-local-sorting="true"
        @sort-changed="sortChanged"
        @row-clicked="rowClicked">

        <template slot="name" slot-scope="data">
          <div class="name-wrapper">
            <!-- v-if is buggy here -->
            <span :class="{ 'hidden' : data.item.type !== 'pdf' || data.item.type !== 'audio' }" class="pdf">
              <i class="fas fa-file-pdf fa-3x"></i>
            </span>
            <span :class="{ 'hidden' : data.item.type === 'pdf' || data.item.type === 'audio' } " class="thumbnail">
              <img v-if="data.item.thumbnail" :src="data.item.thumbnail.url">
              <img v-else :src="data.item.url">
            </span>
            <span class="name">
              {{ data.value }}
            </span>
          </div>
        </template>

        <template slot="size" slot-scope="data">
          {{ readableFilesize(data.value) }}
        </template>

        <template slot="created" slot-scope="data">
          {{ readableDate(data.value) }}
        </template>

        <template slot="actions" slot-scope="data">
          <div class="no-wrap">
            <span v-b-tooltip.hover style="display:inline-block" title="settings">
              <b-button
                v-if="context === 'page'"
                :disabled="fetching"
                class="table-button"
                title="settings"
                variant="outline-secondary"
                @click.stop="edit(data.item.id)">
                <i class="fas fa-cog"></i>
              </b-button>
            </span>
            <span v-b-tooltip.hover style="display:inline-block" title="download">
              <b-button
                v-if="context === 'page'"
                :disabled="fetching"
                class="table-button"
                title="download"
                variant="outline-secondary"
                @click.stop="download(data.item)">
                <i class="fas fa-download"></i>
              </b-button>
            </span>
            <span v-b-tooltip.hover style="display:inline-block" title="delete">
              <b-button
                v-if="context === 'page'"
                :disabled="fetching"
                class="table-button"
                title="delete"
                variant="outline-secondary"
                @click.stop="destroy(data.item.id)">
                <i class="fas fa-trash"></i>
              </b-button>
            </span>
            <span v-b-tooltip.hover style="display:inline-block" title="view">
              <b-button
                v-if="context !== 'page'"
                :disabled="fetching"
                class="table-button"
                title="view"
                variant="outline-secondary"
                @click.stop="viewMedia(data.item)">
                <i class="fas fa-eye"></i>
              </b-button>
            </span>
          </div>
        </template>

      </b-table>

      <b-row>
        <b-col cols="12">
          <b-pagination
            v-if="data.length"
            :disabled="fetching"
            :value="Number(meta.current_page)"
            :total-rows="Number(meta.total)"
            :per-page="Number(meta.per_page)"
            @change="pageChanged"/>
        </b-col>
      </b-row>
      
    </b-container>

    <b-modal v-model="showMediaModal"
             :title="mediaName"
             :size="mediaModalSize" 
             centered
             hide-footer>

      <b-img v-if="mediaType === 'image'" :src="mediaUrl" />

      <div v-if="mediaType === 'video'" class="embed-responsive embed-responsive-16by9">
        <video id="modalVideo" ref="modalVideo" controls="false">
          <source :src="mediaUrl" />
        </video>
      </div>

      <div v-if="mediaType === 'audio'" class="embed-responsive audio-container">
        <audio id="modalAudio" ref="modalAudio" controls="true">
          <source :src="mediaUrl" />
        </audio>
      </div>

    </b-modal>

  </span>
</template>

<script>
import tableMixin from '../../mixins/admin-table'
import downloader from 'downloadjs'

export default {
  mixins: [tableMixin],
  props: {
    context: {
      type: String,
      required: false,
      default: 'page'
    },
    showTypeSelect: {
      type: Boolean,
      required: false,
      default: true
    },
    showModuleSelect: {
      type: Boolean,
      required: false,
      default: true
    },
    idsToHighlight: {
      type: Array,
      required: false,
      default: () => []
    },
    type: {
      type: String,
      required: false,
      default: null
    },
    moduleId: {
      type: String,
      required: false,
      default: null
    }
  },
  data () {
    return {
      formFields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'type', label: 'Type', sortable: false },
        { key: 'created', label: 'Created', sortable: true },
        { key: 'size', label: 'Size', sortable: true },
        { key: 'actions', label: 'Actions' }
      ],
      mediaTypes: [
        { text: 'All Types', value: null },
        { text: 'images', value: 'image' },
        { text: 'videos', value: 'video' },
        { text: 'pdfs', value: 'pdf' },
        { text: 'audio files', value: 'audio' }
      ],
      selectedModule: this.moduleId,
      editRoute: 'admin-edit-media',
      namespace: 'media',
      routeParamName: 'media',
      showMediaModal: false,
      mediaName: '',
      mediaUrl: '',
      mediaType: ''
    }
  },
  computed: {
    tableData () {
      return this.data.map((media) => {
        let _rowVariant = null
        if (this.idsToHighlight.indexOf(media.id) !== -1) {
          _rowVariant = 'success'
        }
        return { ...media, _rowVariant }
      })
    },
    moduleNames () {
      const modules = this.$store.getters['modules/complete']
      const names = modules.map((module) => {
        return {
          text: module.name,
          value: module.id
        }
      })
      names.unshift({text: 'All Modules', value: null})

      return names
    },
    modalVideo () {
      return this.$refs.modalVideo
    },
    modalAudio () {
      return this.$refs.modalAudio
    },
    mediaModalSize () {
      if (this.mediaType === 'audio') {
        return 'md'
      } else {
        return 'lg'
      }
    }
  },
  watch: {
    showMediaModal: function (val) {
      if (!val){
        if (this.mediaType === 'video') {
          this.modalVideo.pause()
          this.modalVideo.currentTime = 0
        }

        this.mediaName = ''
        this.mediaUrl = ''
        this.mediaType = ''
      }      
    }
  },
  methods: {
    // override mixin onMount
    onMount () {
      this.$store.commit(`${this.namespace}/setMetaProp`, {
        view: this.view,
        prop: 'search',
        value: null
      })

      this.$store.commit(`${this.namespace}/setMetaProp`, {
        view: this.view,
        prop: 'current_page',
        value: 1
      })

      const type = this.type ? this.type : null
      this.$store.commit(`${this.namespace}/setMetaProp`, {
        view: this.view,
        prop: 'type',
        value: type
      })

      const module = this.moduleId ? this.moduleId : null
      this.$store.commit(`${this.namespace}/setMetaProp`, {
        view: this.view,
        prop: 'module',
        value: module
      })

      this.$store.dispatch(`${this.namespace}/index`, this.view)
      this.$store.dispatch('modules/indexNoPaginate')
    },
    filterByType (type) {
      this.$store.commit(`${this.namespace}/setMetaProp`, {
        view: this.view,
        prop: 'type',
        value: type
      })
      this.$store.commit(`${this.namespace}/setMetaProp`, {
        view: this.view,
        prop: 'current_page',
        value: 1
      })
      this.$store.dispatch(`${this.namespace}/index`, this.view)
    },
    filterByModule (module) {
      this.$store.commit(`${this.namespace}/setMetaProp`, {
        view: this.view,
        prop: 'module',
        value: module
      })
      this.$store.commit(`${this.namespace}/setMetaProp`, {
        view: this.view,
        prop: 'current_page',
        value: 1
      })
      this.$store.dispatch(`${this.namespace}/index`, this.view)
    },
    rowClicked (item, index, e) {
      if (this.rowClickEnabled) {
        if (this.context === 'page') {
          this.mediaName = item.name
          this.mediaUrl = item.url
          this.mediaType = item.type          
          this.showMediaModal = true
        } else {
          this.$emit('selectMedia', item)
        }
      }
    },
    viewMedia (media) {
      window.open(media.url)
    },
    download (media) {
      const xhr = new XMLHttpRequest()
      xhr.open('GET', media.url , true)
      xhr.responseType = 'blob'
      xhr.onload = (e) => {
        downloader(e.target.response, media.name)
      }
      xhr.send()
    },
    destroy (mediaID) {
      const accepted = confirm('This will permanently delete this item. Are you sure you want to continue?')

      if (accepted) {
        this.$store.dispatch('media/destroy', {
          mediaID
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.name-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;

  .pdf {
    width: 8rem;
    height: 6rem;
    margin-right: 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .thumbnail {
    width: 8rem;
    height: 6rem;
    margin-right: 1rem;
    display: flex;
    justify-content: center;
    align-items: center;

    img {
      width: auto;
      max-width: 8rem;
      height: auto;
      max-height: 6rem;
    }
  }
}
.audio-container {
  display: flex;
  justify-content: center;
  align-items: center 
}
</style>



// WEBPACK FOOTER //
// src/components/pages/admin/media/table.vue